/**
 * Regular expression for validating generic RFCs in Mexico.
 *
 * This regex validates RFCs that can belong to either individuals or companies (legal entities).
 * It supports the general structure of Mexican RFCs with three or four letters, a date, and a homoclave.
 *
 * Format:
 * - The first part consists of 3 or 4 uppercase letters (may include special characters like Ñ and &).
 * - The date is formatted as YYMMDD (year, month, day).
 * - The homoclave can include letters and digits.
 *
 * @constant
 * @type {RegExp}
 * @example
 * // Valid RFC examples
 * const isValidGeneric = GENERIC_RFC_REGEX.test('GODE561231GR8'); // true
 * const isValidGenericCompany = GENERIC_RFC_REGEX.test('ABC1234567X9'); // true
 */
export const GENERIC_RFC_REGEX =
  /^[A-ZÑ&]{3,4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{3}$/;

/**
 * Regular expression for validating RFCs of companies (legal entities) in Mexico.
 *
 * This regex is specifically tailored for companies, which have an RFC format starting with three letters,
 * followed by the date of registration, and a homoclave.
 *
 * Format:
 * - The first part consists of 3 uppercase letters (may include special characters like Ñ and &).
 * - The date is formatted as YYMMDD (year, month, day).
 * - The homoclave can include letters and digits.
 *
 * @constant
 * @type {RegExp}
 * @example
 * // Valid company RFC example
 * const isValidCompanyRFC = COMPANY_RFC_REGEX.test('ABC010203X1'); // true
 */
export const COMPANY_RFC_REGEX =
  /^[A-ZÑ&]{3}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{2}\d$/;

/**
 * Regular expression for validating RFCs of individuals (natural persons) in Mexico.
 *
 * This regex is tailored for individuals, with an RFC format starting with four letters,
 * followed by the date of birth, and a homoclave.
 *
 * Format:
 * - The first part consists of 4 uppercase letters (may include special characters like Ñ and &).
 * - The date is formatted as YYMMDD (year, month, day).
 * - The homoclave can include letters and digits.
 *
 * @constant
 * @type {RegExp}
 * @example
 * // Valid person RFC example
 * const isValidPersonRFC = PERSON_RFC_REGEX.test('GODE561231GR8'); // true
 */
export const PERSON_RFC_REGEX =
  /^[A-ZÑ&]{4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z\d]{2}\d$/;
